import React from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  Link,
  useLocation,
} from "react-router-dom";

import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import PricingPage from "./pages/PricingPage";
import BillingSuccessPage from "./pages/BillingSuccessPage";
import BillingCancelledPage from "./pages/BillingCancelledPage";
import KitchenPage from "./pages/KitchenPage";
import ReportsPage from "./pages/ReportsPage";
import FinanceReportsPage from "./pages/FinanceReportsPage";
import EndOfDayPage from "./pages/EndOfDayPage";
import AdjustmentsPage from "./pages/AdjustmentsPage";
import ManagerHomePage from "./pages/ManagerHomePage";
import RoutinePage from "./pages/RoutinePage";
import RoomsPage from "./pages/RoomsPage";
import RoomTypesPage from "./pages/RoomTypesPage";
import RatePlansPage from "./pages/RatePlansPage";
import FrontDeskBoardPage from "./pages/FrontDeskBoardPage";
import CompaniesPage from "./pages/CompaniesPage";
import ProformasPage from "./pages/ProformasPage";
import NightAuditPage from "./pages/NightAuditPage";
import ForexReportPage from "./pages/ForexReportPage";
import FrontDeskReportsPage from "./pages/FrontDeskReportsPage";
import EventsPage from "./pages/EventsPage";
import ServiceCatalogPage from "./pages/ServiceCatalogPage";
import BackOfficePage from "./pages/BackOfficePage";
import MembersPage from "./pages/MembersPage";
import MembershipPlansPage from "./pages/MembershipPlansPage";
import ServiceBookingsPage from "./pages/ServiceBookingsPage";
import ServiceResourcesPage from "./pages/ServiceResourcesPage";
import ServiceSchedulePage from "./pages/ServiceSchedulePage";
import HandoverPage from "./pages/HandoverPage";
import LostFoundPage from "./pages/LostFoundPage";
import MaintenancePage from "./pages/MaintenancePage";
import ReservationsPage from "./pages/ReservationsPage";
import HousekeepingPage from "./pages/HousekeepingPage";
import RoomsReportPage from "./pages/RoomsReportPage";
import PartnerHotelsPage from "./pages/PartnerHotelsPage";
import OutsourcedReportPage from "./pages/OutsourcedReportPage";
import BusinessOverviewPage from "./pages/BusinessOverviewPage";
import GroupBookingsPage from "./pages/GroupBookingsPage";
import GuestListPage from "./pages/GuestListPage";
import IngredientsPage from "./pages/IngredientsPage";
import RecipesPage from "./pages/RecipesPage";
import UsagePage from "./pages/UsagePage";
import KitchenIssuePage from "./pages/KitchenIssuePage";
import VariancePage from "./pages/VariancePage";
import MenuEngineeringPage from "./pages/MenuEngineeringPage";
import FoodRecoveryPage from "./pages/FoodRecoveryPage";
import GymDashboardPage from "./pages/GymDashboardPage";
import CheckInPage from "./pages/CheckInPage";
import RenewalsPage from "./pages/RenewalsPage";
import ClassesPage from "./pages/ClassesPage";
import PackagesPage from "./pages/PackagesPage";
import ControllerHomePage from "./pages/ControllerHomePage";
import GroupOrdersPage from "./pages/GroupOrdersPage";
import BuffetsPage from "./pages/BuffetsPage";
import ManagerMenuPage from "./pages/ManagerMenuPage";
import ManagerTablesPage from "./pages/ManagerTablesPage";
import QRCodesPage from "./pages/QRCodesPage";
import ManagerStaffPage from "./pages/ManagerStaffPage";
import ManagerAssignmentsPage from "./pages/ManagerAssignmentsPage";
import SchedulePage from "./pages/SchedulePage";
import OwnerLoginPage from "./pages/OwnerLoginPage";
import OwnerDashboardPage from "./pages/OwnerDashboardPage";
import ManagerRatingsPage from "./pages/ManagerRatingsPage";
import ManagerBrandingPage from "./pages/ManagerBrandingPage";
import HotelSettingsPage from "./pages/HotelSettingsPage";
import TenantSettingsPage from "./pages/TenantSettingsPage";
import RecommendationsPage from "./pages/RecommendationsPage";
import LeadsPage from "./pages/LeadsPage";
import TakeOrderPage from "./pages/TakeOrderPage";
import TabsPage from "./pages/TabsPage";
import CashierPage from "./pages/CashierPage";
import MyTillPage from "./pages/MyTillPage";
import TillsOverviewPage from "./pages/TillsOverviewPage";
import SalespersonReportPage from "./pages/SalespersonReportPage";
import PurchaseOrdersPage from "./pages/PurchaseOrdersPage";
import StocktakePage from "./pages/StocktakePage";
import AdminPage from "./pages/AdminPage";
import DeviceLockPage from "./pages/DeviceLockPage";
import MyCodePage from "./pages/MyCodePage";
import RunViewPage from "./pages/RunViewPage";
import AdminGroupsPage from "./pages/AdminGroupsPage";
import SupportPage from "./pages/SupportPage";
import AdminSupportPage from "./pages/AdminSupportPage";
import AdminTeamsPage from "./pages/AdminTeamsPage";
import SecurityPage from "./pages/SecurityPage";
import PrivacyPage from "./pages/PrivacyPage";
import ActivityPage from "./pages/ActivityPage";
import TenantDetailPage from "./pages/TenantDetailPage";
import SplashScreen from "./components/SplashScreen";
import {
  clearSession,
  exitImpersonation,
  getImpersonationMeta,
  getRole,
  getOrgToken,
  getTier,
  getStaffProfile,
  getUser,
  hasFeature,
  isAdmin,
  hasCapability,
  isImpersonating,
  isDeviceMode,
  getToken,
  managerHomePath,
  refreshMe,
  lockToPinScreen,
  openBillingPortal,
  resolveImageUrl,
} from "./services/api";
import { useT } from "./i18n/useT";
import { NavIcon } from "./components/NavIcons";
import OfflineBanner from "./components/OfflineBanner";
import { getDepartments, activeDepartment } from "./services/navConfig";

import "./App.css";

// One always-expanded section of the sidebar: a small uppercase
// heading + a vertical stack of full-width links. Sections inherit the
// same role/feature gating the old dropdowns had — if every item in a
// section is hidden, the whole section (heading included) hides too.
function SidebarSection({ label, items, location, groupOrder, groupLabels }) {
  const visible = items.filter((it) => it.show);
  const grouped = Array.isArray(groupOrder) && groupOrder.length > 0;
  // Which sub-group owns the current route — that group starts expanded
  // so the manager always sees where they are.
  const activeGroup = grouped
    ? (visible.find((it) => location.pathname.startsWith(it.to)) || {}).group
    : null;
  // Per-group open/closed state, remembered across visits. The group
  // holding the active route defaults open; every other group starts
  // collapsed, so a ~13-item Front Desk shrinks to the group in use plus
  // a couple of one-line headers. A manager can toggle any group and the
  // choice sticks (localStorage). Hooks run before the empty-guard below
  // so they are never called conditionally.
  const LS_KEY = "odra_fd_nav_groups";
  const [openMap, setOpenMap] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || "{}"); } catch (e) { return {}; }
  });
  const isOpen = (g) => (openMap[g] === undefined ? g === activeGroup : openMap[g]);
  const toggle = (g) => setOpenMap((prev) => {
    const cur = prev[g] === undefined ? g === activeGroup : prev[g];
    const next = { ...prev, [g]: !cur };
    try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch (e) {}
    return next;
  });

  if (visible.length === 0) return null;

  const grid = (list) => (
    <div className="sidebar-grid">
      {list.map((it) => (
        <Link
          key={it.to}
          to={it.to}
          className={"sidebar-tile" + (location.pathname.startsWith(it.to) ? " active" : "")}
        >
          {it.icon && <NavIcon name={it.icon} />}
          <span className="sidebar-tile-label">{it.label}</span>
        </Link>
      ))}
    </div>
  );
  const headBtn = {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    width: "100%", background: "transparent", border: "none", cursor: "pointer",
    fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase",
    color: "#8a949b", margin: "12px 0 4px", padding: "2px 4px",
  };
  return (
    <div className="sidebar-section">
      <div className="sidebar-heading">{label}</div>
      {grouped ? (
        <>
          {groupOrder.map((g) => {
            const list = visible.filter((it) => it.group === g);
            if (list.length === 0) return null;
            const open = isOpen(g);
            return (
              <div key={g}>
                <button type="button" style={headBtn} onClick={() => toggle(g)} aria-expanded={open}>
                  <span>
                    {(groupLabels && groupLabels[g]) || g}
                    <span style={{ color: "#aab2b8", fontWeight: 400 }}> ({list.length})</span>
                  </span>
                  <span style={{ fontSize: 10, color: "#aab2b8" }}>{open ? "▾" : "▸"}</span>
                </button>
                {open && grid(list)}
              </div>
            );
          })}
          {(() => {
            const rest = visible.filter((it) => !it.group || !groupOrder.includes(it.group));
            return rest.length ? grid(rest) : null;
          })()}
        </>
      ) : grid(visible)}
    </div>
  );
}

function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const role = getRole();
  const user = getUser();
  const staff = getStaffProfile();
  // Translations + language switcher for the staff-facing pages.
  // The admin's per-tenant supported_languages drives which language
  // codes appear in the toggle (set on the Tenant Settings page).
  const { lang, setLang, t, availableLangs, LANG_DISPLAY } = useT();
  const tier = getTier();

  const logout = () => {
    clearSession();
    navigate("/login", { replace: true });
  };

  // Department-grouped nav (see services/navConfig.js). The sidebar
  // expands only the department that owns the current route, plus a
  // compact switcher to hop between departments — this replaces the
  // old "every section at once" layout that overwhelmed managers.
  const departments = getDepartments(role, t);
  const activeDept = activeDepartment(departments, location.pathname);

  // Admin-only links — kept as a dedicated "Admin" section below a
  // divider so founder tooling stays visually separate from the
  // day-to-day restaurant nav.
  const adminItems = [
    { to: "/admin/groups", icon: "building", label: "Groups", show: hasCapability("groups.view") },
    { to: "/admin/leads", icon: "user-plus", label: "Leads", show: hasCapability("leads.view") },
    { to: "/admin/support", icon: "clipboard", label: "Support", show: hasCapability("support.view") },
    { to: "/admin/team", icon: "users", label: "Team", show: hasCapability("team.manage") },
  ];

  return (
    <>
      <div className="sidebar-brand">
        {/* Trimmed transparent horizontal logo — dark-teal artwork that
            sits directly on the cream sidebar, no pill needed. Cache-bust
            suffix (?v=1) forces a refetch when the file changes. */}
        <img
          src="/logo-horizontal.png?v=2"
          alt="ODRA"
          width="170"
          className="sidebar-logo"
        />
        {/* Tier badge — short name keeps the sidebar compact. Long
            display name lives on the Upgrade page. */}
        <span className={`tier-badge tier-${tier}`}>
          {tier === "free" ? "Trial" :
           tier === "starter" ? "Starter" :
           tier === "pro" ? "Professional" :
           tier === "business" ? "Hotel" :
           tier === "enterprise" ? "Enterprise" :
           tier}
        </span>
      </div>
      <nav className="sidebar-nav">
        {(role === "manager" || role === "controller") && (
          <div className="sidebar-section">
            <Link to={managerHomePath()} className={"sidebar-tile home" + (location.pathname === managerHomePath() ? " active" : "")}><NavIcon name="home" size={46} /><span className="sidebar-tile-label">{t("nav_home")}</span></Link>
          </div>
        )}
        {/* Department switcher — one row per department so the manager can
            hop without going back to Home. Hidden when the user only has
            a single department (e.g. a housekeeper). */}
        {departments.length > 1 && (
          <div className="sidebar-section">
            <div className="sidebar-heading">{t("nav_departments")}</div>
            <div className="dept-switch">
              {departments.map((d) => (
                <Link
                  key={d.key}
                  to={d.items[0].to}
                  className={"dept-chip" + (activeDept && activeDept.key === d.key ? " active" : "")}
                >
                  <span className="dept-chip-ico" style={{ background: d.tint, color: d.ink }}>
                    <NavIcon name={d.icon} size={20} />
                  </span>
                  <span className="dept-chip-label">{d.label}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
        {/* The active department's screens. When the route isn't inside
            any department we show nothing here (the switcher stays). */}
        {activeDept && activeDept.items.length > 1 && (
          <SidebarSection label={activeDept.label} items={activeDept.items} location={location} groupOrder={activeDept.groupOrder} groupLabels={activeDept.groupLabels} />
        )}
        {/* Settings (country, languages, branding) is now admin-only —
            managers reach it via the founder who edits per-tenant
            settings at /admin/tenants/:id/settings. Founder HQ "Leads"
            (walkthrough requests from the marketing site) sits in the
            same Admin section so the founder's daily checkpoints
            (stats + new leads) are adjacent in the nav. */}
        {isAdmin() && (
          <>
            <div className="sidebar-divider" aria-hidden="true" />
            <div className="sidebar-section">
              <div className="sidebar-heading">Admin</div>
              <div className="sidebar-grid">
                <Link
                  to="/admin"
                  className={
                    "sidebar-tile" +
                    (location.pathname.startsWith("/admin") &&
                    !location.pathname.startsWith("/admin/leads")
                      ? " active"
                      : "")
                  }
                >
                  <NavIcon name="settings" /><span className="sidebar-tile-label">{t("nav_admin")}</span>
                </Link>
                {adminItems
                  .filter((it) => it.show)
                  .map((it) => (
                    <Link
                      key={it.to}
                      to={it.to}
                      className={
                        "sidebar-tile" +
                        (location.pathname.startsWith(it.to) ? " active" : "")
                      }
                    >
                      {it.icon && <NavIcon name={it.icon} />}
                      <span className="sidebar-tile-label">{it.label}</span>
                    </Link>
                  ))}
              </div>
            </div>
          </>
        )}
      </nav>
      <div className="sidebar-footer">
        {/* Language switcher — only renders when this tenant has more
            than one language configured by the admin. Mirrors the
            customer-app pattern so staff and guests see the same
            three-letter codes. */}
        {availableLangs.length > 1 && (
          <div
            className="lang-pill"
            role="tablist"
            aria-label="Dashboard language"
          >
            {availableLangs.map((code) => (
              <button
                key={code}
                type="button"
                role="tab"
                aria-selected={lang === code}
                className={"lang-pill-btn" + (lang === code ? " active" : "")}
                onClick={() => setLang(code)}
                title={code.toUpperCase()}
              >
                {LANG_DISPLAY[code] || code.toUpperCase()}
              </button>
            ))}
          </div>
        )}
        <div className="sidebar-user" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {staff?.photo_url ? (
            <img
              src={resolveImageUrl(staff.photo_url)}
              alt=""
              style={{ width: 34, height: 34, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
            />
          ) : (
            <div
              style={{ width: 34, height: 34, borderRadius: "50%", background: "#e6eaee", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7484", fontWeight: 700, flexShrink: 0 }}
            >
              {((staff?.name || user?.name || "?").trim().charAt(0) || "?").toUpperCase()}
            </div>
          )}
          <span className="muted" style={{ lineHeight: 1.2 }}>
            {staff?.name || user?.name} · <b style={{ textTransform: "capitalize" }}>{role}</b>
          </span>
        </div>
        <button className="btn secondary" onClick={logout}>
          {t("nav_logout")}
        </button>
      </div>
    </>
  );
}

// App shell: fixed left sidebar + main content shifted right. On
// phones (≤900px) the sidebar slides in as an off-canvas drawer behind
// a slim cream top bar with a hamburger button. The drawer closes on
// route change and on backdrop tap.
function AppShell({ children }) {
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  // On the manager "Today" home, hide the nav behind a menu button so the
  // manager lands on just the home and opens the menu only to go deeper.
  const focused = location.pathname === "/home";

  // Shared-device auto-lock: after 90s idle, drop the staff session and
  // return to the PIN screen (protects per-waiter attribution). No effect
  // for normal logins.
  React.useEffect(() => {
    if (!isDeviceMode()) return;
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => { lockToPinScreen(); window.location.href = "/lock"; }, 90000);
    };
    const evts = ["mousedown", "keydown", "touchstart", "scroll", "mousemove"];
    evts.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => { clearTimeout(timer); evts.forEach((e) => window.removeEventListener(e, reset)); };
  }, []);

  // Idle auto-logout for a normal (non-device) manager session: after 30
  // minutes of inactivity, clear the session and return to login — protects
  // a dashboard left open on a shared/front-desk machine. (Device/PIN mode
  // has its own 90s lock above; owner sessions use a short-lived org token.)
  React.useEffect(() => {
    if (isDeviceMode() || !getToken()) return;
    const IDLE_MS = 30 * 60 * 1000;
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => { clearSession(); window.location.href = "/login"; }, IDLE_MS);
    };
    const evts = ["mousedown", "keydown", "touchstart", "scroll", "mousemove"];
    evts.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => { clearTimeout(timer); evts.forEach((e) => window.removeEventListener(e, reset)); };
  }, []);

  // Refresh entitlements (features / tier / role) on load, so a module the
  // admin just enabled for this venue shows up WITHOUT a full log out / log
  // in. Silent and best-effort: only for a normal logged-in session (not
  // shared-device/PIN mode), and any failure keeps the cached values.
  React.useEffect(() => {
    if (isDeviceMode() || !getToken()) return;
    refreshMe().catch(() => {});
  }, []);

  // Close the mobile drawer whenever navigation happens so the next
  // page renders unobstructed.
  React.useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  return (
    <>
      <OfflineBanner />
      <div className={"app-shell" + (focused ? " focused" : "")}>
      <header className="mobile-topbar">
        <img
          src={focused ? "/logo-horizontal-light.svg" : "/logo-horizontal.png?v=2"}
          alt="ODRA"
          className="mobile-topbar-logo"
        />
        <button
          type="button"
          className="hamburger-btn"
          aria-label="Open navigation menu"
          aria-expanded={drawerOpen}
          onClick={() => setDrawerOpen((o) => !o)}
        >
          <span aria-hidden="true">☰</span>{focused ? <span style={{ marginLeft: 6 }}>Menu</span> : null}
        </button>
      </header>
      {drawerOpen && (
        <div
          className="sidebar-backdrop"
          aria-hidden="true"
          onClick={() => setDrawerOpen(false)}
        />
      )}
      <aside className={"sidebar" + (drawerOpen ? " open" : "")}>
        <Sidebar />
      </aside>
      <main className="main-content">{children}</main>
      </div>
    </>
  );
}

function RequireAuth({ roles, feature, children }) {
  const role = getRole();
  if (!role) return <Navigate to={isDeviceMode() ? "/lock" : "/login"} replace />;
  if (roles && !roles.includes(role)) return <Navigate to="/" replace />;
  if (feature && !hasFeature(feature)) {
    return <Navigate to="/upgrade" replace />;
  }
  return (
    <AppShell>
      <div className="page">{children}</div>
    </AppShell>
  );
}

function RequireAdmin({ children }) {
  if (!getRole()) return <Navigate to="/login" replace />;
  if (!isAdmin()) return <Navigate to="/" replace />;
  return (
    <AppShell>
      <div className="page">{children}</div>
    </AppShell>
  );
}

function Home() {
  const role = getRole();
  // Owner (group) device — signed in as a hotel owner, not branch staff.
  if (!role && getOrgToken()) return <Navigate to="/owner" replace />;
  if (!role) return <Navigate to="/login" replace />;
  if (role === "manager") return <Navigate to={managerHomePath()} replace />;
  if (role === "controller") return <Navigate to="/cost-home" replace />;
  if (role === "store_manager") return <Navigate to="/back-office" replace />;
  if (role === "cashier") return <Navigate to="/cashier" replace />;
  if (role === "front_desk") return <Navigate to="/front-desk/reservations" replace />;
  if (role === "housekeeper" || role === "cleaner") return <Navigate to="/front-desk/housekeeping" replace />;
  if (["therapist", "trainer", "hairdresser", "beautician", "lifeguard"].includes(role)) return <Navigate to="/service-schedule" replace />;
  return <Navigate to="/kitchen" replace />;
}

// Plan display-name mapping. The backend keeps free/pro/business as
// internal tier flags; this maps them to the customer-facing names that
// match the marketing site and the new Pricing page.
const TIER_DISPLAY_NAME = {
  free: "Trial",
  pro: "Professional Subscription",
  business: "Hotel Subscription",
  enterprise: "Enterprise",
};

function UpgradePage() {
  const tier = getTier();
  const onPortal = async () => {
    try {
      const { url } = await openBillingPortal();
      window.location.href = url;
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not open billing portal.");
    }
  };
  const displayName = TIER_DISPLAY_NAME[tier] || tier;
  return (
    <AppShell>
      <div className="page">
        <div className="card" style={{ maxWidth: 620, margin: "40px auto" }}>
          <h1 style={{ marginTop: 0 }}>Upgrade your plan</h1>
          <p className="muted">
            Your current plan: <b>{displayName}</b>
          </p>
          <p>
            ODRA is available as a <b>monthly subscription</b> (fully
            managed by our team) or a <b>one-time licence</b> (dedicated
            deployment). Each path has Professional, Hotel, and
            Enterprise tiers — pricing is tailored to your business size
            and support needs.
          </p>
          <p className="muted" style={{ marginBottom: 16, fontSize: "0.92rem" }}>
            Features like reports, staff accounts, dietary tags, and
            Open Tab Mode are available from the Professional tier
            upward.
          </p>
          <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
            <Link to="/pricing" className="btn">See plans &amp; request quote</Link>
            <a
              href="https://wa.me/31618241608?text=Hi%20ODRA%20team%2C%20I%27d%20like%20to%20upgrade%20my%20plan."
              target="_blank"
              rel="noopener noreferrer"
              className="btn secondary"
            >
              💬 WhatsApp
            </a>
            {tier && tier !== "free" && (
              <button type="button" className="btn secondary" onClick={onPortal}>
                Manage billing
              </button>
            )}
            <Link to="/" className="btn secondary">← Back</Link>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

// Sticky banner shown across the app whenever the admin is impersonating
// a tenant. Clicking 'Exit' restores the admin's saved session and
// reloads to home so the navbar / pages refresh cleanly.
function ImpersonationBanner() {
  if (!isImpersonating()) return null;
  const meta = getImpersonationMeta();
  if (!meta) return null;
  const handleExit = () => {
    if (!window.confirm("Exit impersonation and return to your admin session?")) return;
    exitImpersonation();
    window.location.href = "/admin";
  };
  return (
    <div
      style={{
        position: "sticky", top: 0, zIndex: 200,
        background: "#8a5a00", color: "#fff",
        padding: "8px 16px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 12, flexWrap: "wrap",
        fontSize: "0.9rem", fontWeight: 600,
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
      }}
      role="status"
    >
      <span>
        🎭 Impersonating <b>{meta.target_name}</b>
        <span style={{ fontWeight: 400, marginLeft: 8, opacity: 0.9 }}>
          ({meta.target_email}) — admin: {meta.impersonator}
        </span>
      </span>
      <button
        type="button"
        onClick={handleExit}
        style={{
          background: "#fff", color: "#8a5a00",
          border: "none", padding: "4px 14px",
          borderRadius: 6, fontWeight: 700, cursor: "pointer",
        }}
      >
        Exit impersonation
      </button>
    </div>
  );
}


export default function App() {
  return (
    <>
    <SplashScreen />
    <BrowserRouter>
      <ImpersonationBanner />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/lock" element={<DeviceLockPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/owner/login" element={<OwnerLoginPage />} />
        <Route
          path="/run"
          element={
            <RequireAuth roles={["waiter", "manager", "chef"]}>
              <RunViewPage />
            </RequireAuth>
          }
        />
        <Route
          path="/my-code"
          element={
            <RequireAuth roles={["waiter", "chef", "manager"]}>
              <MyCodePage />
            </RequireAuth>
          }
        />
        <Route path="/owner" element={<OwnerDashboardPage />} />
        <Route
          path="/kitchen"
          element={
            <RequireAuth roles={["chef", "waiter", "manager"]}>
              <KitchenPage />
            </RequireAuth>
          }
        />
        <Route
          path="/take-order"
          element={
            <RequireAuth roles={["waiter", "manager", "cashier"]}>
              <TakeOrderPage />
            </RequireAuth>
          }
        />
        <Route
          path="/tabs"
          element={
            <RequireAuth roles={["waiter", "manager", "chef"]}>
              <TabsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/cashier"
          element={
            <RequireAuth roles={["cashier", "manager"]} feature="open_tab">
              <CashierPage />
            </RequireAuth>
          }
        />
        {/* Cashier till / drawer sessions. /my-till is the cashier's own till
            (manager + cashier); /tills is the manager overview of every till.
            Named /my-till (not /till) so it does NOT prefix-collide with /tills
            in the sidebar's startsWith active-department matching. */}
        <Route
          path="/my-till"
          element={
            <RequireAuth roles={["manager", "cashier"]}>
              <MyTillPage />
            </RequireAuth>
          }
        />
        <Route
          path="/tills"
          element={
            <RequireAuth roles={["manager"]}>
              <TillsOverviewPage />
            </RequireAuth>
          }
        />
        <Route
          path="/salesperson-report"
          element={
            <RequireAuth roles={["manager", "controller"]}>
              <SalespersonReportPage />
            </RequireAuth>
          }
        />
        <Route
          path="/purchasing"
          element={
            <RequireAuth roles={["manager", "controller", "store_manager"]} feature="cost_control">
              <PurchaseOrdersPage />
            </RequireAuth>
          }
        />
        <Route
          path="/stocktake"
          element={
            <RequireAuth roles={["manager", "controller", "store_manager"]} feature="cost_control">
              <StocktakePage />
            </RequireAuth>
          }
        />
        <Route
          path="/end-of-day"
          element={
            <RequireAuth roles={["manager"]}>
              <EndOfDayPage />
            </RequireAuth>
          }
        />
        <Route path="/ingredients" element={<RequireAuth roles={["manager", "controller", "store_manager"]} feature="cost_control"><IngredientsPage /></RequireAuth>} />
        <Route path="/recipes" element={<RequireAuth roles={["manager", "controller"]}><RecipesPage /></RequireAuth>} />
        <Route path="/kitchen-issue" element={<RequireAuth roles={["chef", "manager", "controller"]}><KitchenIssuePage /></RequireAuth>} />
        <Route path="/usage" element={<RequireAuth roles={["chef", "manager", "controller"]}><UsagePage /></RequireAuth>} />
        <Route path="/cost-home" element={<RequireAuth roles={["manager", "controller"]}><ControllerHomePage /></RequireAuth>} />
        <Route path="/cost-variance" element={<RequireAuth roles={["manager", "controller"]}><VariancePage /></RequireAuth>} />
        <Route path="/menu-engineering" element={<RequireAuth roles={["manager", "controller"]}><MenuEngineeringPage /></RequireAuth>} />
        <Route path="/food-recovery" element={<RequireAuth roles={["manager", "controller", "chef"]}><FoodRecoveryPage /></RequireAuth>} />
        <Route path="/gym-home" element={<RequireAuth roles={["manager", "controller"]}><GymDashboardPage /></RequireAuth>} />
        <Route path="/checkin" element={<RequireAuth roles={["manager", "controller", "cashier", "front_desk"]}><CheckInPage /></RequireAuth>} />
        <Route path="/renewals" element={<RequireAuth roles={["manager", "controller", "cashier", "front_desk"]}><RenewalsPage /></RequireAuth>} />
        <Route path="/classes" element={<RequireAuth roles={["manager", "controller", "cashier", "front_desk", "therapist", "trainer", "hairdresser", "beautician", "lifeguard"]}><ClassesPage /></RequireAuth>} />
        <Route path="/packages" element={<RequireAuth roles={["manager", "controller", "cashier", "front_desk"]}><PackagesPage /></RequireAuth>} />
        <Route path="/adjustments" element={<RequireAuth roles={["manager", "controller"]}><AdjustmentsPage /></RequireAuth>} />
        <Route path="/home" element={<RequireAuth roles={["manager", "controller"]}><ManagerHomePage /></RequireAuth>} />
        <Route path="/routine/:which" element={<RequireAuth roles={["manager", "controller"]}><RoutinePage /></RequireAuth>} />
        <Route path="/front-desk/reservations" element={<RequireAuth roles={["manager", "front_desk", "controller"]} feature="front_desk"><ReservationsPage /></RequireAuth>} />
        <Route path="/front-desk/groups" element={<RequireAuth roles={["manager", "front_desk", "controller"]} feature="front_desk"><GroupBookingsPage /></RequireAuth>} />
        <Route path="/front-desk/housekeeping" element={<RequireAuth roles={["manager", "front_desk", "controller", "housekeeper", "cleaner"]} feature="front_desk"><HousekeepingPage /></RequireAuth>} />
        <Route path="/front-desk/board" element={<RequireAuth roles={["manager", "front_desk", "controller"]} feature="front_desk"><FrontDeskBoardPage /></RequireAuth>} />
        <Route path="/front-desk/rooms" element={<RequireAuth roles={["manager", "front_desk", "controller"]} feature="front_desk"><RoomsPage /></RequireAuth>} />
        <Route path="/front-desk/room-types" element={<RequireAuth roles={["manager"]} feature="front_desk"><RoomTypesPage /></RequireAuth>} />
        <Route path="/front-desk/rate-plans" element={<RequireAuth roles={["manager"]} feature="front_desk"><RatePlansPage /></RequireAuth>} />
        <Route path="/front-desk/companies" element={<RequireAuth roles={["manager", "front_desk", "controller"]} feature="front_desk"><CompaniesPage /></RequireAuth>} />
        <Route path="/front-desk/proformas" element={<RequireAuth roles={["manager", "front_desk", "controller"]} feature="front_desk"><ProformasPage /></RequireAuth>} />
        <Route path="/support" element={<RequireAuth roles={["manager", "front_desk", "controller", "chef", "waiter", "cashier"]}><SupportPage /></RequireAuth>} />
        <Route path="/security" element={<RequireAuth roles={["manager", "front_desk", "controller", "chef", "waiter", "cashier"]}><SecurityPage /></RequireAuth>} />
        <Route path="/privacy" element={<RequireAuth roles={["manager"]}><PrivacyPage /></RequireAuth>} />
        <Route path="/hotel-settings" element={<RequireAuth roles={["manager"]}><HotelSettingsPage /></RequireAuth>} />
        <Route path="/activity" element={<RequireAuth roles={["manager", "controller"]}><ActivityPage /></RequireAuth>} />
        <Route path="/admin/support" element={<RequireAuth roles={["manager", "front_desk", "controller", "chef", "waiter", "cashier"]}><AdminSupportPage /></RequireAuth>} />
        <Route path="/admin/team" element={<RequireAuth roles={["manager", "front_desk", "controller", "chef", "waiter", "cashier"]}><AdminTeamsPage /></RequireAuth>} />
        <Route path="/front-desk/night-audit" element={<RequireAuth roles={["manager", "front_desk", "controller"]} feature="front_desk"><NightAuditPage /></RequireAuth>} />
        <Route path="/front-desk/forex-report" element={<RequireAuth roles={["manager", "controller"]} feature="front_desk"><ForexReportPage /></RequireAuth>} />
        <Route path="/front-desk/events" element={<RequireAuth roles={["manager", "front_desk", "controller"]} feature="front_desk"><EventsPage /></RequireAuth>} />
        <Route path="/front-desk/handover" element={<RequireAuth roles={["manager", "front_desk", "controller"]} feature="shift_handover"><HandoverPage /></RequireAuth>} />
        <Route path="/front-desk/reports" element={<RequireAuth roles={["manager", "controller"]} feature="front_desk"><FrontDeskReportsPage /></RequireAuth>} />
        <Route path="/front-desk/rooms-report" element={<RequireAuth roles={["manager", "controller"]} feature="front_desk"><RoomsReportPage /></RequireAuth>} />
        <Route path="/front-desk/partner-hotels" element={<RequireAuth roles={["manager", "front_desk", "controller"]} feature="front_desk"><PartnerHotelsPage /></RequireAuth>} />
        <Route path="/front-desk/outsourced-report" element={<RequireAuth roles={["manager", "controller"]} feature="front_desk"><OutsourcedReportPage /></RequireAuth>} />
        <Route path="/front-desk/guest-list" element={<RequireAuth roles={["manager", "front_desk", "controller"]} feature="front_desk"><GuestListPage /></RequireAuth>} />
        <Route path="/front-desk/lost-found" element={<RequireAuth roles={["manager", "front_desk", "controller"]} feature="front_desk"><LostFoundPage /></RequireAuth>} />
        <Route path="/front-desk/maintenance" element={<RequireAuth roles={["manager", "front_desk", "controller"]} feature="front_desk"><MaintenancePage /></RequireAuth>} />
        <Route path="/group-orders" element={<RequireAuth roles={["manager", "chef", "waiter"]}><GroupOrdersPage /></RequireAuth>} />
        <Route path="/buffets" element={<RequireAuth roles={["manager"]}><BuffetsPage /></RequireAuth>} />
        {/* Merged Services area — Single services + Bundles behind tabs. */}
        <Route path="/services" element={<RequireAuth roles={["manager", "front_desk", "controller"]} feature="services"><ServiceCatalogPage /></RequireAuth>} />
        <Route path="/back-office" element={<RequireAuth roles={["manager", "controller", "store_manager"]} feature="back_office"><BackOfficePage /></RequireAuth>} />
        <Route path="/members" element={<RequireAuth roles={["manager", "front_desk", "controller"]} feature="services"><MembersPage /></RequireAuth>} />
        <Route path="/membership-plans" element={<RequireAuth roles={["manager", "controller"]} feature="services"><MembershipPlansPage /></RequireAuth>} />
        <Route path="/service-bookings" element={<RequireAuth roles={["manager", "front_desk", "cashier", "controller", "therapist", "trainer", "hairdresser", "beautician", "lifeguard"]} feature="services"><ServiceBookingsPage /></RequireAuth>} />
        <Route path="/service-resources" element={<RequireAuth roles={["manager", "controller"]} feature="services"><ServiceResourcesPage /></RequireAuth>} />
        <Route path="/service-schedule" element={<RequireAuth roles={["manager", "front_desk", "cashier", "controller", "therapist", "trainer", "hairdresser", "beautician", "lifeguard"]} feature="services"><ServiceSchedulePage /></RequireAuth>} />
        {/* Business overview (owner / manager cockpit) — balances the
            F&B-heavy analytics by showing Rooms, F&B and Wellness together
            over existing endpoints. Manager + controller, no feature gate
            (each section degrades gracefully if its endpoint is gated). */}
        <Route
          path="/business-overview"
          element={
            <RequireAuth roles={["manager"]}>
              <BusinessOverviewPage />
            </RequireAuth>
          }
        />
        <Route
          path="/reports"
          element={
            <RequireAuth roles={["manager"]} feature="reports">
              <ReportsPage />
            </RequireAuth>
          }
        />
        {/* Finance reports (S1) — read-only fiscal views over the revenue
            ledger. Manager + cashier, no feature gate (mirrors the backend
            /finance-reports role guard). Distinct /finance-reports path so
            its nav active-state does not collide with /reports. */}
        <Route
          path="/finance-reports"
          element={
            <RequireAuth roles={["manager", "cashier"]}>
              <FinanceReportsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/menu"
          element={
            <RequireAuth roles={["manager", "chef"]}>
              <ManagerMenuPage />
            </RequireAuth>
          }
        />
        {/* /tables/qr declared before /tables for clarity; react-router v6
            matches by specificity so order isn't load-bearing. Same
            manager-only guard as /tables. */}
        <Route
          path="/tables/qr"
          element={
            <RequireAuth roles={["manager"]}>
              <QRCodesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/tables"
          element={
            <RequireAuth roles={["manager"]}>
              <ManagerTablesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/staff"
          element={
            <RequireAuth roles={["manager"]} feature="staff_mgmt">
              <ManagerStaffPage />
            </RequireAuth>
          }
        />
        <Route
          path="/assignments"
          element={
            <RequireAuth roles={["manager"]} feature="staff_mgmt">
              <ManagerAssignmentsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/schedule"
          element={
            <RequireAuth roles={["manager"]} feature="scheduling">
              <SchedulePage />
            </RequireAuth>
          }
        />
        <Route
          path="/ratings"
          element={
            <RequireAuth roles={["manager"]} feature="reports">
              <ManagerRatingsPage />
            </RequireAuth>
          }
        />
        {/* Legacy /branding route: kept admin-only so an admin can still
            reach the original ManagerBrandingPage if needed during
            transition. Managers are redirected away (RequireAdmin sends
            them to "/"). The intended modern path is
            /admin/tenants/:id/settings below. */}
        <Route
          path="/branding"
          element={
            <RequireAdmin>
              <ManagerBrandingPage />
            </RequireAdmin>
          }
        />
        <Route
          path="/admin"
          element={
            <RequireAdmin>
              <AdminPage />
            </RequireAdmin>
          }
        />
        <Route
          path="/admin/tenants/:id"
          element={
            <RequireAdmin>
              <TenantDetailPage />
            </RequireAdmin>
          }
        />
        <Route
          path="/admin/leads"
          element={
            <RequireAdmin>
              <LeadsPage />
            </RequireAdmin>
          }
        />
        <Route
          path="/admin/groups"
          element={
            <RequireAdmin>
              <AdminGroupsPage />
            </RequireAdmin>
          }
        />
        <Route
          path="/admin/tenants/:id/settings"
          element={
            <RequireAdmin>
              <TenantSettingsPage />
            </RequireAdmin>
          }
        />
        <Route
          path="/recommendations"
          element={
            <RequireAuth roles={["manager", "chef"]}>
              <RecommendationsPage />
            </RequireAuth>
          }
        />
        <Route path="/upgrade" element={<UpgradePage />} />
        <Route path="/billing/success" element={<BillingSuccessPage />} />
        <Route path="/billing/cancelled" element={<BillingCancelledPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
    </>
  );
}
